import { ArrowUpRight } from "lucide-react";

const benefits = [
  {
    k: "01",
    title: "Live closer to what&rsquo;s actually true.",
    body: "Awakening creativity is mostly about hearing yourself again, under the noise, the should&rsquo;ves, the safe loops. The work follows the listening.",
  },
  {
    k: "02",
    title: "Make work that surprises you again.",
    body: "The best work isn&rsquo;t forced. It arrives slightly sideways, often when you&rsquo;ve stopped trying to be impressive. That muscle is still there, and it remembers you.",
  },
  {
    k: "03",
    title: "Stop performing creativity. Start practicing it.",
    body: "Practice is quieter than performance. It happens before anyone is looking. It&rsquo;s the part that compounds, and the part that brings you back to yourself.",
  },
  {
    k: "04",
    title: "Trust your own small yeses.",
    body: "The reawakening shows up in the choices you stop second-guessing. The walk you take. The piece you start. The yes that finally sounds like yours.",
  },
];

export default function Benefits() {
  return (
    <section
      id="benefits"
      data-testid="benefits-section"
      className="bg-[#FFFFFF] border-b border-[#E5E0D8]"
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-5" data-reveal>
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#1FA5B8] font-body">
              Why this matters
            </div>
            <h2 className="mt-4 font-display text-[#1A1A1A] leading-[1.02] tracking-tight text-[clamp(2.25rem,4.6vw,3.75rem)] font-medium">
              A creative life isn&apos;t a luxury.{" "}
              <span className="italic font-light">It&apos;s</span> a way of
              staying awake.
            </h2>
            <p className="mt-6 font-body text-[#5C5C5C] text-lg leading-relaxed max-w-md">
              The cost of staying asleep is not dramatic. It&apos;s a quiet
              accumulation of years that all start to look the same. The work
              you almost made. The version of you still waiting in the wings.
            </p>

            <div className="mt-10 relative h-72 md:h-96 overflow-hidden border border-[#E5E0D8]">
              <img
                src="https://images.pexels.com/photos/6177633/pexels-photo-6177633.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="A quiet, low-lit studio in morning light"
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="benefits-image"
              />
              <div className="absolute bottom-0 left-0 right-0 p-5 bg-[#F7F5F2]/85 backdrop-blur-sm border-t border-[#E5E0D8]">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#1FA5B8] font-body">
                  The quiet premise
                </div>
                <div className="mt-1 font-display italic text-[#1A1A1A] text-lg leading-snug">
                  &ldquo;The version of you that&apos;s waiting has been
                  patient, but she won&apos;t wait forever.&rdquo;
                </div>
              </div>
            </div>
          </div>

          <ul className="col-span-12 md:col-span-6 md:col-start-7 grid grid-cols-1 gap-y-10">
            {benefits.map((b) => (
              <li
                key={b.k}
                data-testid={`benefit-${b.k}`}
                data-reveal
                className="border-t border-[#1A1A1A] pt-6 group"
              >
                <div className="flex items-baseline justify-between">
                  <span className="font-display text-[36px] leading-none tracking-tight text-[#1A1A1A]">
                    {b.k}
                  </span>
                  <ArrowUpRight
                    className="text-[#1FA5B8] opacity-0 group-hover:opacity-100 transition-opacity"
                    size={20}
                    strokeWidth={1.4}
                  />
                </div>
                <h3
                  className="mt-3 font-display text-[26px] leading-tight tracking-tight text-[#1A1A1A]"
                  dangerouslySetInnerHTML={{ __html: b.title }}
                />
                <p
                  className="mt-3 font-body text-[#5C5C5C] leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: b.body }}
                />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
